import api from '@/lib/api';
import { waitForUndoWindow } from '@/lib/undoSend';

export interface ApiTicket {
  id: string;
  ticket_number: string;
  reference_id: string;
  subject: string;
  description: string;
  status: 'open' | 'pending' | 'solved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  requester_id: string;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
  requester_email: string;
  requester_first_name: string;
  requester_last_name: string;
  assignee_email: string | null;
  assignee_first_name: string | null;
  assignee_last_name: string | null;
  comment_count: string;
  merged_ticket_count?: number;
  merged_ticket_numbers?: string[] | null;
  organization_id: string;
  organization_name: string;
  is_spam: boolean | null;
  tags?: string[];
}

export interface TicketListResponse {
  success: boolean;
  data: {
    tickets: ApiTicket[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface TicketStatsResponse {
  success: boolean;
  data: {
    totalTickets: number;
    openTickets: number;
    pendingTickets: number;
    solvedTickets: number;
    closedTickets: number;
    urgentTickets: number;
    highTickets: number;
    unassignedTickets: number;
  };
}

export interface TicketListParams {
  page?: number;
  limit?: number;
  status?: string | string[]; // Single value, comma-separated string, or array
  priority?: 'low' | 'normal' | 'high' | 'urgent' | 'all';
  assigneeId?: string | string[]; // Single value, comma-separated string, or array (can include 'unassigned')
  organizationId?: string | string[]; // NEW: Multi-select organization filter
  requesterId?: string;
  subject?: string; // Search in ticket subject
  email_content?: string; // Search in ticket comments/email content
  created_at_from?: string; // Date range filter
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
  tags?: string;
  fromEmail?: string;
  slaStatus?: string;
  ticketType?: string;
  sourceType?: string;
  channel?: string;
  updatedBy?: string;
  // NOT operator parameters
  status_not?: string;
  priority_not?: string;
  subject_not?: string;
  email_content_not?: string;
  tags_not?: string;
  assigneeIdNot?: string;
  organizationIdNot?: string;
  fromEmailNot?: string;
  slaStatusNot?: string;
  ticketTypeNot?: string;
  sourceTypeNot?: string;
  sortBy?: 'created_at' | 'updated_at' | 'priority' | 'status' | 'due_date' | 'status_priority_due';
  sortOrder?: 'asc' | 'desc';
}

export interface TicketSearchParams {
  q: string;
  searchInContent?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'created_at' | 'updated_at' | 'priority' | 'status' | 'due_date' | 'status_priority_due';
  sortOrder?: 'asc' | 'desc';
  /**
   * 'autocomplete' — üst bar dropdown'u için hızlı mod:
   *   - yorum içi arama kapalı
   *   - COUNT atlanır
   *   - next_reminder_date / comment_count subquery'leri atlanır
   * 'full' — arama sayfası (default)
   */
  mode?: 'autocomplete' | 'full';
  /** Abort signal — tipik olarak AbortController.signal */
  signal?: AbortSignal;
}

export interface TicketSearchResponse {
  success: boolean;
  data: {
    tickets: (ApiTicket & { matched_content?: string | null })[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    searchInfo: {
      query: string;
      searchInContent: boolean;
      total: number;
    };
  };
}

export interface TicketUpdateData {
  subject?: string;
  status?: 'open' | 'pending' | 'solved' | 'closed';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  assigneeId?: string | null;
  tags?: string[];
}

export interface ApiComment {
  id: string;
  ticket_id: string;
  content: string;
  content_type: string;
  is_public: boolean;
  is_internal: boolean;
  author_id: string;
  email_id: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
  author_email: string;
  author_first_name: string;
  author_last_name: string;
  author_role: string;
  attachments_count: number;
  email_source_type: string | null;
  hasAttachments: boolean;
  attachmentCount: number;
  isEmailComment: boolean;
  emailSourceType: string | null;
  attachmentsUrl: string | null;
}

export interface ApiTicketHistory {
  id: string;
  ticket_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_by_id: string;
  change_reason: string;
  created_at: string;
  metadata: Record<string, any>;
  changed_by_email: string;
  changed_by_first_name: string;
  changed_by_last_name: string;
}

export interface CommentsPagination {
  total: number;
  loaded: number;
  hasMore: boolean;
  nextOffset: number;
}

export interface TicketDetailResponse {
  success: boolean;
  data: {
    ticket: ApiTicket;
    comments: ApiComment[];
    history: ApiTicketHistory[];
    commentsPagination?: CommentsPagination;
  };
}

export interface TicketCommentsResponse {
  success: boolean;
  data: {
    comments: ApiComment[];
    pagination: {
      total: number;
      offset: number;
      limit: number;
      loaded: number;
      hasMore: boolean;
    };
  };
}

export interface ReplyData {
  content: string;
  sendEmail?: boolean;
  attachment_ids?: string[];
  status?: string;
  ticketStatus?: string; // Status field filtrelenirse fallback
}

export interface Attachment {
  id: string;
  filename: string;
  size: number;
  contentType: string;
  downloadUrl: string;
  previewUrl?: string | null;
}

export interface AttachmentListResponse {
  success: boolean;
  data: {
    attachments: Attachment[];
  };
}

export interface AttachmentResponse {
  success: boolean;
  data: Attachment;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    filename: string;
    uploadedAs: string;
    path: string;
    size: number;
    contentType: string;
    uploadedAt: string;
  };
}

export interface ReplyResponse {
  success: boolean;
  message: string;
  data: {
    comment: ApiComment;
    emailSent: boolean;
    attachmentCount?: number;
  };
}

export interface CreateTicketData {
  /** Interne Konversation (org-içi, email'siz) hedef org */
  internalTargetOrgId?: string;
  customerEmail: string;
  customerName?: string;
  subject: string;
  content?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  organizationId: string;
  sendEmail?: boolean;
  attachment_ids?: string[];
}

export interface CreateTicketResponse {
  success: boolean;
  message: string;
  data: {
    ticket: ApiTicket;
  };
}

class TicketsService {
  async getTickets(params?: TicketListParams): Promise<TicketListResponse> {
    console.log('getTickets called with params:', params);
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    // Multi-select status filter: convert array to comma-separated string
    if (params?.status) {
      const statusValue = Array.isArray(params.status)
        ? params.status.join(',')
        : params.status;
      if (statusValue && statusValue !== 'all') {
        searchParams.set('status', statusValue);
      }
    }

    if (params?.priority && params.priority !== 'all') {
      searchParams.set('priority', params.priority);
    }

    // Multi-select assignee filter: convert array to comma-separated string
    if (params?.assigneeId) {
      const assigneeValue = Array.isArray(params.assigneeId)
        ? params.assigneeId.join(',')
        : params.assigneeId;
      if (assigneeValue && assigneeValue !== 'all') {
        searchParams.set('assigneeId', assigneeValue);
      }
    }

    // Multi-select organization filter: convert array to comma-separated string
    if (params?.organizationId) {
      const orgValue = Array.isArray(params.organizationId)
        ? params.organizationId.join(',')
        : params.organizationId;
      if (orgValue && orgValue !== 'all') {
        searchParams.set('organizationId', orgValue);
      }
    }

    if (params?.requesterId) {
      searchParams.set('requesterId', params.requesterId);
    }

    if (params?.subject) {
      searchParams.set('subject', params.subject);
    }

    if (params?.email_content) {
      searchParams.set('email_content', params.email_content);
    }

    if (params?.created_at_from) {
      searchParams.set('created_at_from', params.created_at_from);
    }

    if (params?.created_at_to) {
      searchParams.set('created_at_to', params.created_at_to);
    }

    if (params?.updated_at_from) {
      searchParams.set('updated_at_from', params.updated_at_from);
    }

    if (params?.updated_at_to) {
      searchParams.set('updated_at_to', params.updated_at_to);
    }

    if (params?.tags) {
      searchParams.set('tags', params.tags);
    }

    if (params?.fromEmail) {
      searchParams.set('fromEmail', params.fromEmail);
    }

    if (params?.slaStatus) {
      searchParams.set('slaStatus', params.slaStatus);
    }

    if (params?.ticketType) {
      searchParams.set('ticketType', params.ticketType);
    }

    if (params?.channel) {
      searchParams.set('channel', params.channel);
    }

    if (params?.sourceType) {
      searchParams.set('sourceType', params.sourceType);
    }

    if (params?.updatedBy) {
      searchParams.set('updatedBy', params.updatedBy);
    }

    // NOT operator parameters
    if (params?.status_not) {
      searchParams.set('status_not', params.status_not);
    }

    if (params?.priority_not) {
      searchParams.set('priority_not', params.priority_not);
    }

    if (params?.subject_not) {
      searchParams.set('subject_not', params.subject_not);
    }

    if (params?.email_content_not) {
      searchParams.set('email_content_not', params.email_content_not);
    }

    if (params?.tags_not) {
      searchParams.set('tags_not', params.tags_not);
    }

    if (params?.assigneeIdNot) {
      searchParams.set('assigneeIdNot', params.assigneeIdNot);
    }

    if (params?.organizationIdNot) {
      searchParams.set('organizationIdNot', params.organizationIdNot);
    }

    if (params?.fromEmailNot) {
      searchParams.set('fromEmailNot', params.fromEmailNot);
    }

    if (params?.slaStatusNot) {
      searchParams.set('slaStatusNot', params.slaStatusNot);
    }

    if (params?.ticketTypeNot) {
      searchParams.set('ticketTypeNot', params.ticketTypeNot);
    }

    if (params?.sourceTypeNot) {
      searchParams.set('sourceTypeNot', params.sourceTypeNot);
    }

    if (params?.sortBy) {
      searchParams.set('sortBy', params.sortBy);
    }

    if (params?.sortOrder) {
      searchParams.set('sortOrder', params.sortOrder);
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/tickets?${queryString}` : '/tickets';

    console.log('getTickets URL:', url);
    console.log('getTickets params:', params);
    console.log('getTickets searchParams:', Object.fromEntries(searchParams));

    const response = await api.get<TicketListResponse>(url);
    return response.data;
  }

  async getTicketStats(): Promise<TicketStatsResponse> {
    const response = await api.get<TicketStatsResponse>('/tickets/stats');
    return response.data;
  }

  async getSpamTickets(params?: TicketListParams): Promise<TicketListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    // Multi-select status filter: convert array to comma-separated string
    if (params?.status) {
      const statusValue = Array.isArray(params.status)
        ? params.status.join(',')
        : params.status;
      if (statusValue && statusValue !== 'all') {
        searchParams.set('status', statusValue);
      }
    }

    if (params?.priority && params.priority !== 'all') {
      searchParams.set('priority', params.priority);
    }

    // Multi-select organization filter: convert array to comma-separated string
    if (params?.organizationId) {
      const orgValue = Array.isArray(params.organizationId)
        ? params.organizationId.join(',')
        : params.organizationId;
      if (orgValue && orgValue !== 'all') {
        searchParams.set('organizationId', orgValue);
      }
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/tickets/spam?${queryString}` : '/tickets/spam';

    const response = await api.get<TicketListResponse>(url);
    return response.data;
  }

  async searchTickets(params: TicketSearchParams): Promise<TicketSearchResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('q', params.q);

    if (params.searchInContent) {
      searchParams.append('searchInContent', 'true');
    }

    if (params.page) {
      searchParams.append('page', params.page.toString());
    }

    if (params.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    if (params.mode) {
      searchParams.append('mode', params.mode);
    }

    if (params.sortBy) {
      searchParams.append('sortBy', params.sortBy);
    }

    if (params.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder);
    }

    const response = await api.get<TicketSearchResponse>(
      `/tickets/search?${searchParams.toString()}`,
      params.signal ? { signal: params.signal } : undefined
    );
    return response.data;
  }

  async getTicketById(id: string): Promise<TicketDetailResponse> {
    const response = await api.get<TicketDetailResponse>(`/tickets/${id}`);
    return response.data;
  }

  async getTicketComments(ticketId: string, offset: number = 0, limit: number = 10): Promise<TicketCommentsResponse> {
    const response = await api.get<TicketCommentsResponse>(`/tickets/${ticketId}/comments?offset=${offset}&limit=${limit}`);
    return response.data;
  }

  async updateTicket(id: string, data: TicketUpdateData): Promise<{ success: boolean; data: ApiTicket }> {
    const response = await api.put<{ success: boolean; data: ApiTicket }>(`/tickets/${id}`, data);
    return response.data;
  }

  async bulkUpdateTickets(ticketIds: string[], data: TicketUpdateData): Promise<{ success: boolean }> {
    // Since API doesn't have bulk update, we'll update each ticket individually
    const promises = ticketIds.map(id => this.updateTicket(id, data));
    await Promise.all(promises);
    return { success: true };
  }

  async deleteTickets(ticketIds: string[]): Promise<{ success: boolean }> {
    // Delete multiple tickets by calling delete endpoint for each
    const deletePromises = ticketIds.map(async (ticketId) => {
      const response = await api.delete(`/tickets/${ticketId}`);
      return response.data;
    });

    await Promise.all(deletePromises);
    return { success: true };
  }

  async deleteTicket(ticketId: string): Promise<{ success: boolean; message: string; data: any }> {
    const response = await api.delete<{ success: boolean; message: string; data: any }>(`/tickets/${ticketId}`);
    return response.data;
  }

  async markAsSpam(ticketIds: string[], isSpam: boolean = true): Promise<{
    success: boolean;
    message: string;
    data: { updatedCount: number; tickets: ApiTicket[] }
  }> {
    const response = await api.put<{
      success: boolean;
      message: string;
      data: { updatedCount: number; tickets: ApiTicket[] }
    }>('/tickets/bulk/spam', {
      ticketIds,
      isSpam
    });
    return response.data;
  }

  async mergeTickets(ticketIds: string[]): Promise<{ success: boolean }> {
    // API doesn't specify merge endpoint, so we'll mock it for now
    console.log('Merge tickets:', ticketIds);
    return { success: true };
  }

  async replyToTicket(id: string, data: ReplyData): Promise<ReplyResponse> {
    // „Senden rückgängig machen“ (falls eingeschaltet): nur echte, nach außen gehende Antworten.
    if ((data as any).sendEmail !== false && !(data as any).isInternal) {
      await waitForUndoWindow('Antwort');
    }
    const response = await api.post<ReplyResponse>(`/tickets/${id}/reply`, data);
    console.log('✅ tickets.service response received:', response.status);
    return response.data;
  }

  async getAttachments(emailId: string): Promise<AttachmentListResponse> {
    const response = await api.get<AttachmentListResponse>(`/attachments/email/${emailId}`);
    return response.data;
  }

  async getAttachmentsByUrl(attachmentsUrl: string): Promise<AttachmentListResponse> {
    // Support both /api/attachments/email/:emailId and /api/attachments/comment/:commentId
    const response = await api.get<AttachmentListResponse>(attachmentsUrl);
    return response.data;
  }

  async getAttachment(attachmentId: string): Promise<AttachmentResponse> {
    const response = await api.get<AttachmentResponse>(`/attachments/${attachmentId}`);
    return response.data;
  }

  async getTicketGallery(ticketId: string): Promise<{
    success: boolean;
    data: {
      gallery: Array<{
        ticketId: string;
        ticketNumber: string;
        ticketSubject: string;
        isExternal: boolean;
        attachments: Array<{
          id: string;
          filename: string;
          contentType: string;
          sizeBytes: number;
          s3Key: string;
          createdAt: string;
          isInline: boolean;
          sourceType: 'email' | 'note' | 'comment';
          previewUrl: string | null;
          downloadUrl: string;
        }>;
      }>;
      totalAttachments: number;
      mainTicketId: string;
    };
  }> {
    const response = await api.get(`/tickets/${ticketId}/gallery`);
    return response.data;
  }

  async uploadAttachment(file: File): Promise<UploadResponse> {
    console.log('TicketsService: Starting upload for file:', file.name, file.type, file.size);

    const formData = new FormData();
    formData.append('file', file);

    console.log('TicketsService: FormData created, making API call to /attachments/upload');

    try {
      // Axios automatically sets Content-Type for FormData, don't override it
      const response = await api.post<UploadResponse>('/attachments/upload', formData);
      console.log('TicketsService: Upload successful, response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('TicketsService: Upload failed:', error);
      console.error('TicketsService: Error response:', error.response);
      console.error('TicketsService: Error status:', error.response?.status);
      console.error('TicketsService: Error data:', error.response?.data);
      throw error;
    }
  }

  async createTicket(data: CreateTicketData): Promise<CreateTicketResponse> {
    const response = await api.post<CreateTicketResponse>('/tickets', data);
    return response.data;
  }

  async updateSpamStatus(ticketId: string, isSpam: boolean): Promise<{ success: boolean; message: string; data: { ticket: ApiTicket } }> {
    const response = await api.put<{ success: boolean; message: string; data: { ticket: ApiTicket } }>(`/tickets/${ticketId}/spam`, { isSpam });
    return response.data;
  }

  /**
   * reassign=true verschiebt Tickets, die bereits in einem ANDEREN Haupt-Ticket
   * stecken, hierher (sonst 409 mit der vollständigen Konfliktliste).
   */
  async mergeTickets(parentTicketId: string, childTicketIds: string[], reassign = false): Promise<{
    success: boolean;
    message: string;
    data: {
      parentTicket: { id: string; ticket_number: string };
      mergedTickets: Array<{ id: string; ticket_number: string; movedFrom?: { id: string; ticket_number: string } | null }>;
      alreadyMerged?: Array<{ id: string; ticket_number: string }>;
    };
  }> {
    const response = await api.post('/tickets/merge', {
      parentTicketId,
      childTicketIds,
      reassign
    });
    return response.data;
  }

  async getMergeSuggestions(ticketId: string): Promise<{
    success: boolean;
    data: {
      suggestions: Array<{
        id: string;
        ticket_number: string;
        subject: string;
        status: string;
        created_at: string;
        updated_at: string;
        reason: 'order' | 'email';
        shared_orders: string[];
        merged_count: number;
        suggested_parent: string;
      }>;
    };
  }> {
    const response = await api.get(`/tickets/${ticketId}/merge-suggestions`);
    return response.data;
  }

  async getMergedTickets(ticketId: string): Promise<{
    success: boolean;
    data: {
      parentTicket: { id: string; ticket_number: string };
      mergedTickets: ApiTicket[];
      count: number;
    };
  }> {
    const response = await api.get(`/tickets/${ticketId}/merged`);
    return response.data;
  }

  async unmergeTicket(ticketId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      ticketId: string;
      parentTicketId: string;
    };
  }> {
    const response = await api.put(`/tickets/${ticketId}/unmerge`);
    return response.data;
  }

  async promoteToParent(ticketId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      newParentId: string;
      oldParentId: string;
      affectedTickets: number;
    };
  }> {
    const response = await api.put(`/tickets/${ticketId}/promote-to-parent`);
    return response.data;
  }

  async getAdjacentTickets(
    ticketId: string,
    viewParams?: Record<string, string>
  ): Promise<{
    success: boolean;
    data: {
      previous: { id: string; ticketNumber: string } | null;
      next: { id: string; ticketNumber: string } | null;
    };
  }> {
    // Pass all view params to backend - supports all filter types including NOT operators
    const params = viewParams ? { ...viewParams, useViewFilters: 'true' } : {};
    const response = await api.get(`/tickets/${ticketId}/adjacent`, { params });
    return response.data;
  }

  /**
   * Batch count for multiple custom views in a single request
   * Much more efficient than calling getTickets for each view
   */
  async batchCount(views: Array<{
    viewId: string;
    filterConfig: any;
  }>): Promise<{
    success: boolean;
    data: {
      counts: Record<string, number>;
      fetchedAt: string;
    };
  }> {
    const response = await api.post('/tickets/batch-count', { views });
    return response.data;
  }
}

export default new TicketsService();
